/**
 * Created by Pasquale Tritella on 22/04/2024
 *
 * @format
 */


/**
 * Invia una richiesta di rateizzazione al server.
 * Payload atteso dall'endpoint POST /installment (fonte di verità: v1.py di Gianmaria):
 *   { payments_notice_id, number_installments, expiration_interval, expiration_day, date }
 * La validazione strict lato backend risponde 422 con { error, message } se i parametri
 * sono fuori dai vincoli del cliente (installment_config).
 *
 * NB: niente `mode:'no-cors'` — rendeva la response opaca (non leggibile) e impediva
 * di intercettare gli errori 422. La CORS è gestita dal backend (allowedOrigin).
 *
 * @param {Object} body
 * @returns {Promise<{ok:boolean, data?:Object, status?:number, error?:string, message?:string}>}
 */
const postInstallment = async (body) => {
    const hash = localStorage.getItem('hash');
    if (hash === null) {
        console.error("NO_AUTH");
        return { ok: false, error: 'NO_AUTH' };
    }
    try {
        const response = await fetch(window.route.installment, {
            method: 'POST',
            headers: { 'x-access-token': hash, 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            // 422 = validazione strict (NUM_RATE_OUT_OF_RANGE, INTERVAL_NOT_ALLOWED, ...)
            // 401 = token mancante/scaduto/non valido: chk_token risponde { response: 'Expired'|'Invalid'|'Unauthorized' },
            // shape diversa da { error, message } — la esponiamo comunque come `error` così il chiamante può distinguerla.
            console.warn('postInstallment KO', response.status, data);
            return { ok: false, status: response.status, error: data.error || data.response, message: data.message };
        }
        console.log('postInstallment OK', data);
        return { ok: true, data };
    } catch (error) {
        console.error('postInstallment network error:', error);
        return { ok: false, error: 'NETWORK', message: error.message };
    }
};

/**
 * Carica allegati su una richiesta di rateazione già creata (step 2 del flusso).
 * Payload atteso da POST /installment_attachment (v1.py di Gianmaria):
 *   { richiesta_rateazione_id, attachments: [{ filename, mimetype, content_base64 }] }
 * Vincoli backend: mimetype in {pdf, jpeg, png}, max 5 file, 5MB l'uno. La pre-validazione
 * fallita risponde 422 INVALID_ATTACHMENT; l'ownership è verificata via x-access-token.
 *
 * @param {Object} body
 * @returns {Promise<{ok:boolean, data?:Object, status?:number, error?:string, message?:string}>}
 */
const postInstallmentAttachment = async (body) => {
    const hash = localStorage.getItem('hash');
    if (hash === null) {
        console.error("NO_AUTH");
        return { ok: false, error: 'NO_AUTH' };
    }
    try {
        const response = await fetch(window.route.installmentAttachment, {
            method: 'POST',
            headers: { 'x-access-token': hash, 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            // 401 = token mancante/scaduto/non valido: chk_token risponde { response: 'Expired'|'Invalid'|'Unauthorized' },
            // shape diversa da { error, message } — la esponiamo comunque come `error` così il chiamante può distinguerla.
            console.warn('postInstallmentAttachment KO', response.status, data);
            return { ok: false, status: response.status, error: data.error || data.response, message: data.message };
        }
        console.log('postInstallmentAttachment OK', data);
        return { ok: true, data };
    } catch (error) {
        console.error('postInstallmentAttachment network error:', error);
        return { ok: false, error: 'NETWORK', message: error.message };
    }
};

const InstallmentApi = {
    postInstallment: postInstallment,
    postInstallmentAttachment: postInstallmentAttachment
};

export default InstallmentApi;
